import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert,
  Chip, TextField, Divider, MenuItem, Select, FormControl, InputLabel, IconButton, Menu,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import {
  listMyProgramMemberships, listProgramSeasonTeams, getProgram,
  listProgramSeasons, createSeason, listSeasonDivisions, createDivision, updateDivision,
  createTeamInDivision, assignTeamToDivision, moveTeamToDivision, archiveTeam, reactivateTeam,
} from '../../services/programService';
import { seasonStatusLabel, seasonStatusChipStyle } from '../../utils/seasonStatus';
import { divisionGradeLabel, competitionTypeLabel, competitionTypeChipStyle } from '../../utils/divisionDisplay';
import { classificationDisplay } from '../../utils/teamClassification';

const CURRENT_YEAR = new Date().getFullYear();

// Phase C.1 — the Program/Season/Division/Team hierarchy admin view.
// Deliberately a SEPARATE page from ProgramDashboardPage.js (which
// groups every Season Team by season_status/season for the existing
// Setup/Current/History workflow, untouched by this phase) rather than
// a rework of it — this page's job is purely "see and build the
// organizational tree," not season-lifecycle management. "Do not spend
// this phase making this beautiful" (phase brief) — plain nested
// cards/lists, no accordion/drag-reorder UI.

/** Inline "+ Create Team" form for one Division — the lean path (name/
 * city/state/zip only, migration 036's create_team_in_division()). No
 * classification/football-type step here; that's an optional follow-up
 * edit via the Team's own Settings page, unchanged. */
function AddTeamForm({ divisionId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ teamName: '', city: '', state: '', zipCode: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.teamName || !form.city || !form.state || !form.zipCode) {
      setError('Team name, city, state, and ZIP are required.');
      return;
    }
    setSaving(true);
    try {
      await createTeamInDivision({ divisionId, ...form });
      setForm({ teamName: '', city: '', state: '', zipCode: '' });
      setOpen(false);
      await onAdded();
    } catch (e2) {
      setError(e2.message || 'Could not create team.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button size="small" onClick={() => setOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, pl: 0 }}>
        + Add Team
      </Button>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start', mt: 1 }}>
      <TextField size="small" label="Team name" value={form.teamName} onChange={set('teamName')} sx={{ minWidth: 160 }} />
      <TextField size="small" label="City" value={form.city} onChange={set('city')} sx={{ minWidth: 120 }} />
      <TextField size="small" label="State" value={form.state} onChange={set('state')} inputProps={{ maxLength: 2 }} sx={{ width: 80 }} />
      <TextField size="small" label="ZIP" value={form.zipCode} onChange={set('zipCode')} sx={{ width: 100 }} />
      <Button type="submit" size="small" variant="contained" disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}>
        {saving ? <CircularProgress size={16} sx={{ color: '#050c16' }} /> : 'Add'}
      </Button>
      <Button size="small" onClick={() => setOpen(false)} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
        Cancel
      </Button>
      {error && <Alert severity="error" sx={{ width: '100%', mt: 0.5 }}>{error}</Alert>}
    </Box>
  );
}

/** Phase C.4a — moves an ALREADY-assigned Team to a different Division
 * within the SAME Season, current Division excluded from the target
 * list. Uses moveTeamToDivision() (042) ONLY — never falls back to
 * assignTeamToDivision() on failure, per this phase's own explicit
 * instruction. The backend's own error ("This Team already has Division
 * matchups and cannot be moved.") is already the exact user-facing
 * wording this phase asked for — shown verbatim, no database
 * terminology to translate. */
function MoveTeamDialog({ team, divisions, open, onClose, onMoved }) {
  const [targetDivisionId, setTargetDivisionId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setTargetDivisionId(''); setError(null); }
  }, [open, team]);

  if (!team) return null;
  const eligibleDivisions = divisions.filter(d => d.id !== team.division_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetDivisionId) { setError('Choose a destination Division.'); return; }
    setError(null);
    setSaving(true);
    try {
      await moveTeamToDivision({ teamId: team.id, divisionId: targetDivisionId });
      await onMoved();
      onClose();
    } catch (e2) {
      setError(e2.message || 'Could not move this team.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="xs" fullWidth
      PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
    >
      <DialogTitle sx={{ fontWeight: 900 }}>Move {team.team_name}</DialogTitle>
      <DialogContent>
        <Box component="form" id="move-team-form" onSubmit={handleSubmit} sx={{ mt: 0.5 }}>
          {eligibleDivisions.length === 0 ? (
            <Typography sx={{ color: '#9eb8d0' }}>No other Divisions exist in this Season yet.</Typography>
          ) : (
            <TextField
              select fullWidth label="Move to Division" value={targetDivisionId}
              onChange={e => setTargetDivisionId(e.target.value)} required
            >
              {eligibleDivisions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          )}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Cancel</Button>
        <Button
          type="submit" form="move-team-form" variant="contained" color="primary"
          disabled={saving || eligibleDivisions.length === 0} sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Move Team'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Phase C.4a — a Team row is now actionable: Open Team/Team Settings
 * (existing pages, unchanged), Move Team (new, guarded), Archive/
 * Reactivate (028's existing safe RPCs, no new capability invented).
 * Delete deliberately NOT offered here — the only safe delete path
 * (delete_setup_team, Owner-only, Setup-stage-only) stays on the Team's
 * own page. Status badge reuses seasonStatusLabel/seasonStatusChipStyle
 * verbatim — no new lifecycle system. */
function TeamRow({ team, divisions, onChanged, navigate }) {
  const [anchor, setAnchor] = useState(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const isArchived = team.status === 'archived';

  const handleArchiveToggle = async () => {
    setAnchor(null);
    setActionError(null);
    setArchiving(true);
    try {
      if (isArchived) await reactivateTeam(team.id);
      else await archiveTeam(team.id);
      await onChanged();
    } catch (e) {
      setActionError(e.message || 'Could not update this team.');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <Box sx={{ py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, opacity: isArchived ? 0.6 : 1 }}>
        <Typography sx={{ fontWeight: 600 }}>{team.team_name}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Chip size="small" label={seasonStatusLabel(team.season_status)} sx={{ fontWeight: 700, ...seasonStatusChipStyle(team.season_status) }} />
          {isArchived && (
            <Chip size="small" label="Archived" sx={{ fontWeight: 700, bgcolor: '#2a1405', color: '#f5a623' }} />
          )}
          <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} disabled={archiving} sx={{ color: '#6a8aa8' }}>
            <Typography sx={{ fontWeight: 900, lineHeight: 1 }}>⋯</Typography>
          </IconButton>
        </Box>
      </Box>
      {actionError && <Alert severity="error" sx={{ mt: 0.5 }}>{actionError}</Alert>}

      <Menu
        anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.97)', border: '1px solid #2a3a4e' } }}
      >
        <MenuItem onClick={() => { setAnchor(null); navigate(`/my-teams/${team.id}`); }}>Open Team</MenuItem>
        <MenuItem onClick={() => { setAnchor(null); navigate(`/my-teams/${team.id}/settings`); }}>Team Settings</MenuItem>
        <MenuItem onClick={() => { setAnchor(null); setMoveOpen(true); }}>Move Team</MenuItem>
        <MenuItem onClick={handleArchiveToggle}>{isArchived ? 'Reactivate Team' : 'Archive Team'}</MenuItem>
      </Menu>

      <MoveTeamDialog team={team} divisions={divisions} open={moveOpen} onClose={() => setMoveOpen(false)} onMoved={onChanged} />
    </Box>
  );
}

/** Phase C.4a — Edit Division. Every field here is a real, already-
 * mutable column via update_division() (036) — nothing new is invented.
 * Pre-filled from the current Division, same validation shape as
 * AddDivisionForm's own create path (grade min/max together, 0-12,
 * players/side 4-15) so the RPC's own errors are rarely the first thing
 * an admin sees. Status is a simple Active/Archived toggle — the only
 * two values divisions.status actually supports (036). */
function EditDivisionDialog({ division, open, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && division) {
      setForm({
        name: division.name || '',
        competitionType: division.competition_type || 'other',
        gradeMin: division.grade_min ?? '',
        gradeMax: division.grade_max ?? '',
        classification: division.classification || '',
        playersPerSide: division.players_per_side ?? '',
        displayOrder: division.display_order ?? '',
        status: division.status || 'active',
      });
      setError(null);
    }
  }, [open, division]);

  if (!form) return null;
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) { setError('Division name is required.'); return; }
    if ((form.gradeMin === '') !== (form.gradeMax === '')) {
      setError('Grade min and grade max must be provided together.'); return;
    }
    setSaving(true);
    try {
      await updateDivision({
        divisionId: division.id,
        name: form.name,
        competitionType: form.competitionType,
        gradeMin: form.gradeMin === '' ? null : Number(form.gradeMin),
        gradeMax: form.gradeMax === '' ? null : Number(form.gradeMax),
        classification: form.classification || null,
        playersPerSide: form.playersPerSide === '' ? null : Number(form.playersPerSide),
        displayOrder: form.displayOrder === '' ? null : Number(form.displayOrder),
        status: form.status,
      });
      await onSaved();
      onClose();
    } catch (e2) {
      setError(e2.message || 'Could not update division.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.98)', border: '1px solid #1e2a3a' } }}
    >
      <DialogTitle sx={{ fontWeight: 900 }}>Edit Division</DialogTitle>
      <DialogContent>
        <Box component="form" id="edit-division-form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start', mt: 0.5 }}>
          <TextField size="small" label="Name" value={form.name} onChange={set('name')} sx={{ minWidth: 160 }} />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={form.competitionType} onChange={set('competitionType')}>
              <MenuItem value="in_house">In-House</MenuItem>
              <MenuItem value="travel">Travel</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Grade min" type="number" value={form.gradeMin} onChange={set('gradeMin')} sx={{ width: 100 }} />
          <TextField size="small" label="Grade max" type="number" value={form.gradeMax} onChange={set('gradeMax')} sx={{ width: 100 }} />
          <TextField size="small" label="Classification (JV, Varsity, ...)" value={form.classification} onChange={set('classification')} sx={{ minWidth: 180 }} />
          <TextField size="small" label="Players/side" type="number" value={form.playersPerSide} onChange={set('playersPerSide')} sx={{ width: 110 }} />
          <TextField size="small" label="Display order" type="number" value={form.displayOrder} onChange={set('displayOrder')} sx={{ width: 110 }} />

          <Box sx={{ width: '100%' }}>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>Status</Typography>
            <ToggleButtonGroup
              value={form.status} exclusive size="small"
              onChange={(_e, v) => { if (v !== null) setForm(f => ({ ...f, status: v })); }}
            >
              <ToggleButton value="active" sx={{ textTransform: 'none', fontWeight: 700 }}>Active</ToggleButton>
              <ToggleButton value="archived" sx={{ textTransform: 'none', fontWeight: 700 }}>Archived</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#9eb8d0' }}>Cancel</Button>
        <Button type="submit" form="edit-division-form" variant="contained" color="primary" disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DivisionCard({ division, teams, allDivisions, programId, programSlug, onChanged, navigate }) {
  const divisionTeams = teams.filter(t => t.division_id === division.id);
  const [editOpen, setEditOpen] = useState(false);
  // Phase C.4a — the existing Division public-page model, unchanged:
  // Program publicly visible + this Division's own status = 'active'
  // (038/042's own public RPCs already enforce exactly this — no new
  // publication boolean). Rendered only when both halves are already
  // true, same "never a dead public link" discipline as every other
  // View Public Page action in this app.
  const canViewPublicPage = Boolean(programSlug) && division.status === 'active';

  return (
    <Card sx={{ backgroundColor: 'rgba(8,16,28,0.6)', border: '1px solid #16202e', mb: 1.5 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 800 }}>{division.name}</Typography>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem' }}>{divisionGradeLabel(division)}</Typography>
          </Box>
          <Chip size="small" label={competitionTypeLabel(division.competition_type)} sx={{ fontWeight: 700, ...competitionTypeChipStyle(division.competition_type) }} />
        </Box>

        <Divider sx={{ my: 1 }} />

        {divisionTeams.length === 0 ? (
          <Typography sx={{ color: '#4a6080', fontSize: '0.85rem' }}>No teams yet.</Typography>
        ) : (
          divisionTeams.map(t => <TeamRow key={t.id} team={t} divisions={allDivisions} onChanged={onChanged} navigate={navigate} />)
        )}

        <Box sx={{ mt: 1 }}>
          <AddTeamForm divisionId={division.id} onAdded={onChanged} />
        </Box>

        <Divider sx={{ my: 1.25 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            size="small" onClick={() => navigate(`/my-programs/${programId}/divisions/${division.id}/schedule`)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#6a8aa8', pl: 0 }}
          >
            Open Division
          </Button>
          {canViewPublicPage && (
            <Button
              size="small" onClick={() => navigate(`/programs/${programSlug}/divisions/${division.id}`)}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#6a8aa8' }}
            >
              View Public Page ↗
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setEditOpen(true)} sx={{ textTransform: 'none', fontWeight: 600, color: '#6a8aa8' }}>
            Edit Division
          </Button>
        </Box>
      </CardContent>

      <EditDivisionDialog division={division} open={editOpen} onClose={() => setEditOpen(false)} onSaved={onChanged} />
    </Card>
  );
}

/** Inline "+ Create Division" form for one Season. Grade min/max and
 * classification are both optional and independent — see migration
 * 036's own header (a Division may set either, both, or neither). */
function AddDivisionForm({ seasonId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', competitionType: 'in_house', gradeMin: '', gradeMax: '', classification: '', playersPerSide: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Division name is required.');
      return;
    }
    setSaving(true);
    try {
      await createDivision({
        seasonId,
        name: form.name,
        competitionType: form.competitionType,
        gradeMin: form.gradeMin === '' ? null : Number(form.gradeMin),
        gradeMax: form.gradeMax === '' ? null : Number(form.gradeMax),
        classification: form.classification || null,
        playersPerSide: form.playersPerSide === '' ? null : Number(form.playersPerSide),
      });
      setForm({ name: '', competitionType: 'in_house', gradeMin: '', gradeMax: '', classification: '', playersPerSide: '' });
      setOpen(false);
      await onAdded();
    } catch (e2) {
      setError(e2.message || 'Could not create division.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button size="small" variant="outlined" onClick={() => setOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
        + Create Division
      </Button>
    );
  }

  return (
    <Card sx={{ backgroundColor: 'rgba(8,16,28,0.6)', border: '1px solid #16202e', mb: 1.5 }}>
      <CardContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField size="small" label="Name" value={form.name} onChange={set('name')} placeholder="2nd–3rd Grade" sx={{ minWidth: 160 }} />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select label="Type" value={form.competitionType} onChange={set('competitionType')}>
              <MenuItem value="in_house">In-House</MenuItem>
              <MenuItem value="travel">Travel</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" label="Grade min" type="number" value={form.gradeMin} onChange={set('gradeMin')} sx={{ width: 100 }} />
          <TextField size="small" label="Grade max" type="number" value={form.gradeMax} onChange={set('gradeMax')} sx={{ width: 100 }} />
          <TextField size="small" label="Classification (JV, Varsity, ...)" value={form.classification} onChange={set('classification')} sx={{ minWidth: 180 }} />
          <TextField size="small" label="Players/side" type="number" value={form.playersPerSide} onChange={set('playersPerSide')} sx={{ width: 110 }} />
          <Button type="submit" variant="contained" disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}>
            {saving ? <CircularProgress size={16} sx={{ color: '#050c16' }} /> : 'Create'}
          </Button>
          <Button onClick={() => setOpen(false)} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
            Cancel
          </Button>
        </Box>
        <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mt: 1 }}>
          Grade min/max represent a single grade (e.g. 6 and 6) or a combined range (e.g. 2 and 3 for 2nd–3rd). Use 0 for Kindergarten. Classification (JV/Varsity/custom) does not require a grade range.
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </CardContent>
    </Card>
  );
}

/** Existing Program Teams not yet assigned to any Division under this
 * Season — the on-ramp for Teams created before Divisions existed (or
 * via any other still-live creation path) to join the hierarchy. */
function UnassignedTeams({ season, teams, divisions, onChanged }) {
  const unassigned = teams.filter(t => t.season === season.year && !t.division_id);
  const [assigning, setAssigning] = useState(null); // team id currently being assigned
  const [error, setError] = useState(null);

  if (unassigned.length === 0) return null;

  const handleAssign = async (teamId, divisionId) => {
    if (!divisionId) return;
    setAssigning(teamId);
    setError(null);
    try {
      await assignTeamToDivision({ teamId, divisionId });
      await onChanged();
    } catch (e) {
      setError(e.message || 'Could not assign team.');
    } finally {
      setAssigning(null);
    }
  };

  return (
    <Card sx={{ backgroundColor: 'rgba(42,20,5,0.35)', border: '1px solid #4a2a05', mb: 1.5 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography sx={{ fontWeight: 700, color: '#f5a623', mb: 1 }}>
          Unassigned {season.year} Teams
        </Typography>
        {unassigned.map(t => (
          <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.5, flexWrap: 'wrap' }}>
            {/* Phase C.3.1 — same "classification + name" hierarchy hint
                already used by the private Program Dashboard's own
                SeasonTeamCard, so two same-named unassigned Teams (e.g.
                two "Blackhawks" from different real grade levels) can
                still be told apart while picking a Division for each. */}
            <Typography sx={{ fontWeight: 600 }}>
              {classificationDisplay(t) ? `${classificationDisplay(t)} ${t.team_name}` : t.team_name}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                displayEmpty value=""
                disabled={assigning === t.id || divisions.length === 0}
                onChange={e => handleAssign(t.id, e.target.value)}
              >
                <MenuItem value="" disabled>Assign to Division…</MenuItem>
                {divisions.map(d => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ))}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </CardContent>
    </Card>
  );
}

function SeasonSection({ season, teams, programId, programSlug, navigate, onChanged }) {
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDivisions = useCallback(async () => {
    const rows = await listSeasonDivisions(season.id);
    setDivisions(rows);
  }, [season.id]);

  useEffect(() => {
    setLoading(true);
    loadDivisions().finally(() => setLoading(false));
  }, [loadDivisions]);

  const handleChanged = async () => {
    await loadDivisions();
    await onChanged();
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 900, fontSize: '1.1rem' }}>{season.name}</Typography>
        <Chip size="small" label={season.status} sx={{ fontWeight: 700, bgcolor: '#1a2e44', color: '#9eb8d0', textTransform: 'capitalize' }} />
      </Box>

      {loading ? (
        <CircularProgress size={20} sx={{ color: '#2ea84b' }} />
      ) : (
        <>
          <UnassignedTeams season={season} teams={teams} divisions={divisions} onChanged={handleChanged} />
          {divisions.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.9rem', mb: 1.5 }}>No divisions yet.</Typography>
          ) : (
            divisions.map(d => (
              <DivisionCard
                key={d.id} division={d} teams={teams} allDivisions={divisions}
                programId={programId} programSlug={programSlug} navigate={navigate}
                onChanged={handleChanged}
              />
            ))
          )}
          <AddDivisionForm seasonId={season.id} onAdded={handleChanged} />
        </>
      )}
    </Box>
  );
}

/** Inline "+ Create Season" form — page-level, not per-season. */
function AddSeasonForm({ programId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const yearNum = Number(year);
    if (!yearNum || yearNum < 2000 || yearNum > 2100) {
      setError('Enter a valid four-digit year.');
      return;
    }
    setSaving(true);
    try {
      await createSeason({ programId, year: yearNum });
      setOpen(false);
      await onAdded();
    } catch (e2) {
      setError(e2.message || 'Could not create season.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button variant="contained" color="primary" onClick={() => setOpen(true)} sx={{ textTransform: 'none', fontWeight: 800, px: 3, mb: 3 }}>
        + Create Season
      </Button>
    );
  }

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mb: 3 }}>
      <CardContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <TextField size="small" label="Year" type="number" value={year} onChange={e => setYear(e.target.value)} sx={{ width: 120 }} />
          <Button type="submit" variant="contained" disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, height: 40 }}>
            {saving ? <CircularProgress size={16} sx={{ color: '#050c16' }} /> : 'Create Season'}
          </Button>
          <Button onClick={() => setOpen(false)} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
            Cancel
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </CardContent>
    </Card>
  );
}

function ProgramSeasonsInner() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [program, setProgram] = useState(null);
  // Phase C.4a — same best-effort, independent slug lookup already
  // established on the Program Dashboard/Team Settings pages; a failure
  // here only means Division cards never render "View Public Page."
  const [programSlug, setProgramSlug] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!currentUser) return;
    const [memberships, seasonRows, teamRows] = await Promise.all([
      listMyProgramMemberships(),
      listProgramSeasons(programId),
      listProgramSeasonTeams(programId),
    ]);
    setProgram(memberships.find(p => p.id === programId) || null);
    setSeasons(seasonRows);
    setTeams(teamRows);
    getProgram(programId).then(p => setProgramSlug(p?.slug || null)).catch(() => setProgramSlug(null));
  }, [programId, currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    setLoading(true);
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [load, currentUser]);

  const sortedSeasons = useMemo(() => [...seasons].sort((a, b) => b.year - a.year), [seasons]);

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!program) return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Alert severity="error">Program not found, or you don't have Program-level access to it.</Alert>
    </Container>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button onClick={() => navigate(`/my-programs/${programId}`)} sx={{ textTransform: 'none', color: '#6a8aa8', mb: 1, pl: 0 }}>
        ← {program.name}
      </Button>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>Seasons &amp; Divisions</Typography>
      <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
        Build the age/grade groupings under each Season, then add Teams inside them.
      </Typography>

      <AddSeasonForm programId={programId} onAdded={load} />

      {sortedSeasons.length === 0 ? (
        <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography sx={{ color: '#9eb8d0' }}>No Seasons yet. Create one above to get started.</Typography>
          </CardContent>
        </Card>
      ) : (
        sortedSeasons.map(s => (
          <SeasonSection
            key={s.id} season={s} teams={teams}
            programId={programId} programSlug={programSlug} navigate={navigate}
            onChanged={load}
          />
        ))
      )}
    </Container>
  );
}

export default function ProgramSeasonsPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><ProgramSeasonsInner /></RequireAuth>
    </Box>
  );
}
